// src/screens/MyPass.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../styles/MyPass.css";

/**
 * ✅ IMPORTANT:
 * - do NOT include /api in REACT_APP_API_URL
 * - Capacitor origin is capacitor://localhost (can't be used as API host)
 */
const API_ORIGIN = (() => {
  const clean = (v) =>
    (v || "")
      .trim()
      .replace(/\/+$/, "")
      .replace(/\/api$/, "");

  const env = clean(process.env.REACT_APP_API_URL);
  if (env) return env;

  if (typeof window === "undefined") return "";

  const origin = window.location.origin || "";
  const isCapacitor = origin.startsWith("capacitor://");
  const isLocalhost = /localhost|127\.0\.0\.1/.test(origin);

  // ✅ When running in Capacitor, ALWAYS call your real API host
  if (isCapacitor || isLocalhost) return "https://pay2pee.onrender.com";

  // Web prod (pay2pee.app) can hit same origin if backend is proxied;
  // but your backend is onrender, so we default to it.
  return "https://pay2pee.onrender.com";
})();

const API_BASE = API_ORIGIN ? `${API_ORIGIN}/api` : "/api";

function getSessionIdFromUrl() {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("session_id");
  if (fromQuery) return fromQuery;

  // hash router support: /#/mypass?session_id=...
  const hash = window.location.hash || "";
  const idx = hash.indexOf("?");
  if (idx !== -1) {
    const qs = hash.slice(idx + 1);
    const hashParams = new URLSearchParams(qs);
    return hashParams.get("session_id");
  }
  return null;
}

const toMillis = (ts) => {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (typeof ts === "string") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  if (ts?._seconds) return ts._seconds * 1000;
  if (typeof ts?.toMillis === "function") return ts.toMillis();
  return null;
};

const formatTime = (seconds) => {
  if (seconds === null || seconds === undefined) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export default function MyPass() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [passData, setPassData] = useState(null);
  const [error, setError] = useState("");

  const [visitStatus, setVisitStatus] = useState(null);
  const [visitId, setVisitId] = useState(null);

  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [graceSecondsRemaining, setGraceSecondsRemaining] = useState(null);

  const sessionId = useMemo(() => getSessionIdFromUrl(), []);

  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  // Build from paymentsRoutes.js response shape
  const buildFromSessionPayload = (payload) => {
    const visit = payload?.visit || null;
    const location = payload?.location || null;

    if (!visit && !location) return null;

    const businessName = visit?.locationName || location?.name || "Restroom";
    const address = visit?.locationAddress || location?.address || "";

    return {
      businessName,
      address,
      status: visit?.status || payload?.session?.status || "pending",
      accessCode: visit?.accessCode || location?.accessCode || null,
      instructions: visit?.instructions || location?.instructions || null,

      startTime: visit?.startTime || null,
      expiresAt: visit?.expiresAt || null,
      maxDurationMinutes: visit?.maxDurationMinutes ?? 8,
      passType: visit?.passType || "one-time",

      queuePosition: payload?.queuePosition ?? null,
      guestsAhead: payload?.guestsAhead ?? null,
      graceSecondsRemaining: payload?.graceSecondsRemaining ?? null,
    };
  };

  const fetchPassData = async () => {
    setLoading(true);
    setError("");

    try {
      // ✅ Step 1: ALWAYS try session endpoint when sessionId exists
      // It is NOT protected and it creates the visit doc if paid.
      let sessionPayload = null;
      if (sessionId) {
        const sessionRes = await axios.get(
          `${API_BASE}/payments/guest/session/${sessionId}`
        );
        sessionPayload = sessionRes.data || null;
      }

      // If we got something from session route, show it (works even without token)
      if (sessionPayload) {
        const pass = buildFromSessionPayload(sessionPayload);

        // visit id lives on visitDoc.id in your route response
        const v = sessionPayload.visit || null;

        setPassData(pass);
        setVisitStatus(pass?.status || null);
        setVisitId(v?.id || null);

        // grace tick
        if (pass?.graceSecondsRemaining != null) {
          setGraceSecondsRemaining(Number(pass.graceSecondsRemaining));
        } else {
          setGraceSecondsRemaining(null);
        }

        // remaining tick
        const expiresMs = toMillis(pass?.expiresAt);
        const ended =
          pass?.status === "completed" ||
          pass?.status === "expired" ||
          pass?.status === "ended";

        if (ended) setRemainingSeconds(0);
        else if (expiresMs) {
          setRemainingSeconds(
            Math.max(0, Math.floor((expiresMs - Date.now()) / 1000))
          );
        } else {
          setRemainingSeconds(null);
        }

        setLoading(false);
        return;
      }

      // ✅ Step 2: If no sessionId, MyPass requires login
      if (!token) {
        setError("Please sign in to view your pass.");
        setPassData(null);
        setVisitStatus(null);
        setVisitId(null);
        setRemainingSeconds(null);
        setGraceSecondsRemaining(null);
        setLoading(false);
        return;
      }

      // Optional: if you have /api/guest/visits/current in another router, use it here.
      // If you DON'T, just keep relying on sessionId flows.
      const res = await axios.get(`${API_BASE}/guest/visits/current`, {
        headers: authHeader,
      });

      const visit = res.data?.visit || null;
      if (!visit) {
        setPassData(null);
        setVisitId(null);
        setVisitStatus("ended");
        setRemainingSeconds(0);
        setGraceSecondsRemaining(null);
        setLoading(false);
        return;
      }

      // Build from guest visit current response
      const pass = {
        businessName: visit.locationName || "Restroom",
        address: visit.locationAddress || "",
        status: visit.status || "pending",
        accessCode: visit.accessCode || null,
        instructions: visit.instructions || null,
        startTime: visit.startTime || null,
        expiresAt: visit.expiresAt || null,
        passType: visit.passType || "one-time",
        maxDurationMinutes: visit.maxDurationMinutes ?? 8,
        queuePosition: visit.queuePosition ?? null,
        guestsAhead: visit.guestsAhead ?? null,
        graceSecondsRemaining: visit.graceSecondsRemaining ?? null,
      };

      setPassData(pass);
      setVisitId(visit.id || null);
      setVisitStatus(pass.status);

      if (pass.graceSecondsRemaining != null) {
        setGraceSecondsRemaining(Number(pass.graceSecondsRemaining));
      } else {
        setGraceSecondsRemaining(null);
      }

      const expiresMs = toMillis(pass.expiresAt);
      const ended =
        pass.status === "completed" || pass.status === "expired" || pass.status === "ended";

      if (ended) setRemainingSeconds(0);
      else if (expiresMs) {
        setRemainingSeconds(Math.max(0, Math.floor((expiresMs - Date.now()) / 1000)));
      } else {
        setRemainingSeconds(null);
      }

      setLoading(false);
    } catch (err) {
      console.error("MyPass fetch error:", err);
      setError(err.response?.data?.error || "Unable to load your pass. Please try again.");
      setPassData(null);
      setVisitStatus(null);
      setRemainingSeconds(null);
      setGraceSecondsRemaining(null);
      setVisitId(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPassData();
    
  }, [sessionId, token]);

  // refresh while pending/requested
  useEffect(() => {
    if (!visitStatus) return;

    const isPending =
      visitStatus === "requested" ||
      visitStatus === "pending" ||
      visitStatus === "unpaid" ||
      visitStatus === "open";

    if (!isPending) return;

    const t = setInterval(fetchPassData, 10000);
    return () => clearInterval(t);
    
  }, [visitStatus]);

  // active countdown tick
  useEffect(() => {
    if (remainingSeconds === null) return;
    if (remainingSeconds === 0) return;

    const t = setInterval(() => {
      setRemainingSeconds((s) => (s == null ? null : s > 0 ? s - 1 : 0));
    }, 1000);

    return () => clearInterval(t);
  }, [remainingSeconds]);

  // grace countdown tick
  useEffect(() => {
    if (graceSecondsRemaining === null) return;
    if (graceSecondsRemaining === 0) return;

    const t = setInterval(() => {
      setGraceSecondsRemaining((s) => (s == null ? null : s > 0 ? s - 1 : 0));
    }, 1000);

    return () => clearInterval(t);
  }, [graceSecondsRemaining]);

  const statusText =
    visitStatus === "active"
      ? "active"
      : visitStatus === "completed" || visitStatus === "expired" || visitStatus === "ended"
      ? "ended"
      : "pending";

  const showMakeYourWay = graceSecondsRemaining != null && graceSecondsRemaining > 0;

  const canEnd =
    Boolean(token) &&
    Boolean(visitId) &&
    (visitStatus === "active" || visitStatus === "requested" || visitStatus === "pending");

  const handleEndPass = async () => {
    try {
      setError("");

      if (!token || !visitId) {
        setError("Please sign in to end your pass.");
        return;
      }

      await axios.post(
        `${API_BASE}/payments/guest/visit/${visitId}/end`,
        {},
        { headers: authHeader }
      );

      setRemainingSeconds(0);
      setVisitStatus("completed");
      await fetchPassData();
    } catch (e) {
      console.error("End pass failed:", e);
      setError(e?.response?.data?.error || "Could not end pass. Please try again.");
    }
  };

  const handleBackHome = () => {
    if (token) navigate("/home");
    else navigate("/");
  };

  const handleSignIn = () => navigate("/auth");

  if (loading) {
    return (
      <div className="mypass-page">
        <div className="mypass-card">
          <h2 className="mypass-title">Your Pass</h2>
          <p className="mypass-muted">Loading your pass…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mypass-page">
        <div className="mypass-card mypass-card--error">
          <h2 className="mypass-title">Your Pass</h2>
          <p className="mypass-muted">{error}</p>

          <div className="mypass-section mypass-section--actions">
            <button className="mypass-primary-btn" onClick={fetchPassData}>
              Try Again
            </button>

            {!token ? (
              <button className="mypass-primary-btn" onClick={handleSignIn}>
                Sign In
              </button>
            ) : null}
          </div>

          <div className="mypass-footer">
            <button className="mypass-back-link" onClick={handleBackHome}>
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!passData) {
    return (
      <div className="mypass-page">
        <div className="mypass-card mypass-card--error">
          <h2 className="mypass-title">Your Pass</h2>
          <p className="mypass-muted">No active pass right now.</p>
          <div className="mypass-footer">
            <button className="mypass-back-link" onClick={handleBackHome}>
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mypass-page">
      <div className="mypass-card" role="region" aria-label="Your pass">
        <div className="mypass-top">
          <h2 className="mypass-title">Your Pass</h2>

          <div
            className={[
              "mypass-status-pill",
              statusText === "active"
                ? "mypass-status--active"
                : statusText === "pending"
                ? "mypass-status--pending"
                : "mypass-status--error",
            ].join(" ")}
          >
            Status: {statusText}
          </div>
        </div>

        {showMakeYourWay ? (
          <div className="mypass-grace-banner">
            <div className="mypass-grace-title">Next: Make your way to the restroom</div>
            <div className="mypass-grace-sub">
              You have <strong>{formatTime(graceSecondsRemaining)}</strong> before your time starts.
            </div>
          </div>
        ) : null}

        <div className="mypass-section">
          <h3 className="mypass-section-title">{passData.businessName}</h3>
          {passData.address ? <p className="mypass-address">{passData.address}</p> : null}
        </div>

        <div className="mypass-section mypass-section--code">
          <p className="mypass-section-label">Access Code</p>
          {passData.accessCode ? (
            <div className="mypass-code-box">{passData.accessCode}</div>
          ) : (
            <p className="mypass-instructions">
              We’re still generating your access code. This page will refresh automatically.
            </p>
          )}
        </div>

        <div className="mypass-section mypass-section--meta">
          {remainingSeconds !== null ? (
            <div className="mypass-meta-row">
              <span className="mypass-meta-label">Time left</span>
              <span className="mypass-meta-value">{formatTime(remainingSeconds)}</span>
            </div>
          ) : null}

          {passData.passType ? (
            <div className="mypass-meta-row">
              <span className="mypass-meta-label">Pass type</span>
              <span className="mypass-meta-value">{passData.passType}</span>
            </div>
          ) : null}

          {passData.maxDurationMinutes != null ? (
            <div className="mypass-meta-row">
              <span className="mypass-meta-label">Max duration</span>
              <span className="mypass-meta-value">{passData.maxDurationMinutes} min</span>
            </div>
          ) : null}

          {passData.queuePosition != null ? (
            <div className="mypass-meta-row">
              <span className="mypass-meta-label">Queue position</span>
              <span className="mypass-meta-value">{passData.queuePosition}</span>
            </div>
          ) : null}

          {passData.guestsAhead != null ? (
            <div className="mypass-meta-row">
              <span className="mypass-meta-label">Guests ahead</span>
              <span className="mypass-meta-value">{passData.guestsAhead}</span>
            </div>
          ) : null}
        </div>

        <div className="mypass-section mypass-section--actions">
          <button className="mypPass-message-button" onClick={fetchPassData}>
            Refresh Pass
          </button>

          {canEnd ? (
            <button className="mypass-end-btn" onClick={handleEndPass}>
              End Pass
            </button>
          ) : null}

          {!token ? (
            <button className="mypass-end-btn" onClick={handleSignIn}>
              Sign in to manage pass
            </button>
          ) : null}
        </div>

        <div className="mypass-footer">
          <button className="mypass-back-link" onClick={handleBackHome}>
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}